import React from 'react';

export default function StepHeader({ stepLabels, currentStep, maxStepReached, draftName, draftMeta, onSaveExit, onJumpToStep }) {
  // Default: only steps before the current one are considered reached.
  // Wizard passes its own value; for an opened saved valuation it seeds
  // to stepLabels.length so all pips are clickable from step 1.
  const reachedCeiling = typeof maxStepReached === 'number' ? maxStepReached : currentStep;
  return (
    <div style={containerStyle}>
      <div style={draftInfoStyle}>
        {draftMeta && <div style={draftMetaStyle}>{draftMeta}</div>}
        <div style={draftNameStyle}>
          {draftName || 'Untitled valuation'}
        </div>
      </div>
      <div style={pipsWrapperStyle}>
        <div style={pipRowStyle}>
          {stepLabels.map((label, i) => {
            const num = i + 1;
            const state = num < currentStep ? 'done' : num === currentStep ? 'active' : 'upcoming';
            // Clickable when it's any step (past or future) the user has
            // already reached this session. Saved valuations seed
            // maxStepReached to the last step so the whole row is
            // navigable; new valuations only unlock as the user advances.
            const interactive =
              num !== currentStep
              && num <= reachedCeiling
              && typeof onJumpToStep === 'function';
            const content = (
              <>
                <span style={pipNumStyle(state)}>{num}.</span>
                <span>{label}</span>
              </>
            );
            return (
              <React.Fragment key={label}>
                {i > 0 && <span style={dividerStyle} aria-hidden="true" />}
                {interactive ? (
                  <button
                    type="button"
                    onClick={() => onJumpToStep(num)}
                    style={pipButtonStyle(state)}
                    data-step={num}
                    data-state={state}
                    aria-label={`Jump back to step ${num}: ${label}`}
                  >
                    {content}
                  </button>
                ) : (
                  <span style={pipStyle(state)} data-step={num} data-state={state}>
                    {content}
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      {onSaveExit && (
        <button type="button" style={saveExitStyle} onClick={onSaveExit}>
          Save
        </button>
      )}
    </div>
  );
}

const containerStyle = {
  padding: '18px 32px',
  background: 'var(--bg)',
  borderBottom: '1px solid var(--line)',
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
};

const draftInfoStyle = { minWidth: 0 };
const draftMetaStyle = { fontSize: '11.5px', color: 'var(--muted)' };
const draftNameStyle = {
  fontSize: '17px',
  fontWeight: 600,
  letterSpacing: '-0.015em',
  marginTop: '2px',
  color: 'var(--ink)',
};

const pipsWrapperStyle = { flex: 1, display: 'flex', justifyContent: 'center' };
const pipRowStyle = { display: 'flex', alignItems: 'center', gap: '6px' };
const dividerStyle = { width: '12px', height: '1px', background: 'var(--line)' };

function pipStyle(state) {
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    padding: '6px 12px',
    borderRadius: '999px',
    fontSize: '12.5px',
  };
  if (state === 'active') return { ...base, background: 'var(--ink)', color: 'var(--bg)', fontWeight: 500 };
  if (state === 'done') return { ...base, color: 'var(--ink)' };
  return { ...base, color: 'var(--muted)' };
}
function pipButtonStyle(state) {
  return {
    ...pipStyle(state),
    background: 'transparent',
    border: '1px solid var(--line)',
    cursor: 'pointer',
    fontFamily: 'var(--font)',
  };
}
function pipNumStyle(state) {
  const c = state === 'active' ? 'var(--bg)' : state === 'done' ? 'var(--accent)' : 'inherit';
  return { fontVariantNumeric: 'tabular-nums', fontSize: '12px', color: c };
}

const saveExitStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: '32px',
  padding: '0 14px',
  border: '1px solid var(--line)',
  borderRadius: '999px',
  background: 'transparent',
  color: 'var(--ink2)',
  fontSize: '12.5px',
  cursor: 'pointer',
  fontFamily: 'var(--font)',
};
